import React from 'react';
import Icon from '../../../components/AppIcon';
import { formatDZ } from '../../../utils/currency';
import Button from '../../../components/ui/Button';

const RecentOrders = ({ orders, onViewOrder, onReorder, onTrackOrder }) => {
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'text-success bg-success/10 border-success/30';
      case 'shipped':
        return 'text-primary bg-primary/10 border-primary/30';
      case 'processing':
        return 'text-accent bg-accent/10 border-accent/30';
      case 'cancelled':
        return 'text-error bg-error/10 border-error/30';
      default:
        return 'text-muted-foreground bg-muted/10 border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'CheckCircle';
      case 'shipped':
        return 'Truck';
      case 'processing':
        return 'Clock';
      case 'cancelled':
        return 'XCircle';
      default:
        return 'Package';
    }
  };

  return (
    <div className="holographic-card p-6 rounded-lg">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold command-text">Recent Orders</h2>
        <Button variant="outline" size="sm">
          View All Orders
        </Button>
      </div>
      <div className="space-y-4">
        {orders?.map((order) => (
          <div
            key={order?.id}
            className="border border-border rounded-lg p-4 hover:bg-muted/20 transition-all duration-200"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <span className="font-mono text-sm text-muted-foreground">#{order?.id}</span>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(order?.status)}`}
                  >
                    <Icon name={getStatusIcon(order?.status)} size={12} className="inline mr-1" />
                    {order?.status}
                  </span>
                </div>

                <div className="flex items-center space-x-4 mb-3">
                  <div className="w-12 h-12 bg-muted rounded-lg overflow-hidden">
                    <img
                      src={order?.items?.[0]?.image}
                      alt={order?.items?.[0]?.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = '/assets/images/no_image.png';
                      }}
                    />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{order?.items?.[0]?.name}</p>
                    {order?.items?.length > 1 && (
                      <p className="text-sm text-muted-foreground">
                        +{order?.items?.length - 1} more item{order?.items?.length > 2 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">
                    Ordered on {new Date(order.orderDate)?.toLocaleDateString()}
                  </span>
                  <span className="font-bold text-foreground">{formatDZ(order?.total)}</span>
                </div>
              </div>

              <div className="flex flex-col space-y-2 ml-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onViewOrder(order?.id)}
                  iconName="Eye"
                  iconPosition="left"
                >
                  View
                </Button>
                {order?.status?.toLowerCase() === 'delivered' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onReorder(order?.id)}
                    iconName="RotateCcw"
                    iconPosition="left"
                  >
                    Reorder
                  </Button>
                )}
                {['shipped', 'processing']?.includes(order?.status?.toLowerCase()) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onTrackOrder(order?.id)}
                    iconName="MapPin"
                    iconPosition="left"
                  >
                    Track
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecentOrders;